import { IRunningTotal } from "./../../../../../../motw_core/DTOs/IRunningTotal";
import { IRelatedBookOfMindQuotesResponse } from "./../../../../../../motw_core/DTOs/IRelatedBookOfMindQuotesResponse";
import { IBookOfMindSpeechCacheStats } from "motw_core/DTOs/IBookOfMindSpeechCacheStats";
import { ICalendarEvent } from "motw_core/Calendar/ICalendarEvent";
import { IEmotionRecordDTO } from "motw_core/DTOs/IEmotionRecordDTO";
import { IKeyValueAverageDTO } from "motw_core/DTOs/IKeyValueAverageDTO";
import {
    IDailySpend,
    resourceNameDailySpend,
} from "motw_core/Entity/IDailySpend";
import { IKeyValueLog } from "motw_core/Entity/IKeyValueLog";
import { IWorkflowNode } from "motw_core/Entity/IWorkflowNode";
import { ESharedFunction } from "motw_core/Enums/ESharedFunction";
import { IRecentRateChange } from "motw_core/RecentRateChanges/IRecentRateChange";
import ClientDataProvider from "source/app/services/External/ClientDataProvider/ClientDataProvider";
import { IBookOfMindPageAudioDiagnostics } from "./IBookOfMindPageAudioDiagnostics";
import { IGoalStreakInfoDTO } from "../../../../../../motw_core/DTOs/GoalStreakInfo/IGoalStreakInfoDTO";
import { ICICODailyCaloriesRecordDTO } from "../../../../../../motw_core/DTOs/ICICODailyCaloriesRecordDTO";
import { IDiversityDateDTO } from "../../../../../../motw_core/DTOs/IDiversityDateDTO";
import { IGameIdentityDTO } from "../../../../../../motw_core/DTOs/IGameIdentityDTO";
import { IPlayerKipsTodayDTO } from "../../../../../../motw_core/DTOs/IPlayerKipsTodayDTO";
import { IRecentEmotionsDTO } from "../../../../../../motw_core/DTOs/IRecentEmotionsDTO";
import { IUserDTO } from "../../../../../../motw_core/DTOs/IUserDTO";
import { IFingerprintDTO } from "../../../../../../motw_core/DTOs/fingerprint";
import { IYoutubeDownloadResult } from "../../../../../../motw_core/YouTube/IYoutubeDownloadResult";
import { IYoutubeSearchResult } from "../../../../../../motw_core/YouTube/IYoutubeSearchResult";
import StaticSingleton from "../../Shared/StaticSingleton";
import {
    SavedPasteFileResponse,
    UploadPastedFileAsync,
} from "./UploadPastedFile";

export type BookOfMindPageSpeechJobStatus =
    | "preparing"
    | "ready"
    | "error";

export interface IBookOfMindPageSpeechJob {
    jobId: string;
    status: BookOfMindPageSpeechJobStatus;
    completedSteps?: number;
    totalSteps?: number;
    error?: string;
    clientRequestId?: string;
}

// TOOD: Extract all remaining methods from class to module scope
// and get rid of class
class MotwLogicServer extends StaticSingleton<MotwLogicServer> {
    constructor() {
        super();
    }

    UploadPastedFile = (dataUrl: string, originalName?: string) =>
        UploadPastedFileAsync({
            dataUrl,
            originalName,
        });

    GetSimplifiedCalendarEvents = (): Promise<ICalendarEvent[]> =>
        ClientDataProvider.GetValueAsync(
            "calendar/fastmail/simplified",
            (data) => data.data,
            (error) => {
                throw error;
            }
        );

    UpdateCalendarEventAsync = (
        calendarEvent: Partial<ICalendarEvent>
    ): Promise<any> =>
        ClientDataProvider.PostValueAsync(
            "calendar/fastmail/event/update",
            calendarEvent
        );

    DeleteCalendarEventAsync = (
        calendarEvent: Partial<ICalendarEvent>
    ): Promise<any> =>
        ClientDataProvider.PostValueAsync(
            "calendar/fastmail/event/delete",
            calendarEvent
        );

    CreateCalendarEventAsync = (
        calendarEvent: Partial<ICalendarEvent>
    ): Promise<any> =>
        ClientDataProvider.PostValueAsync(
            "calendar/fastmail/event/create",
            calendarEvent
        );

    GetKipsEarnedToday = (): Promise<IPlayerKipsTodayDTO> =>
        ClientDataProvider.GetValueAsync(`player/kips/today`);

    GetPlayerDaysInGame = (): Promise<number> =>
        ClientDataProvider.GetValueAsync(
            `player/daysInGame`,
            (data) => data.daysInGame
        );

    GetGameIdentityScores = (date: string) =>
        ClientDataProvider.GetValueAsync<IGameIdentityDTO[]>(
            `player/identities/identityScores?date=${date}`
        );

    CheckFingerprint = (): Promise<IFingerprintDTO> =>
        ClientDataProvider.GetValueAsync(`auth/device/fingerprint`);

    GetRecentRateChanges = (): Promise<IRecentRateChange[]> =>
        ClientDataProvider.GetValueAsync(
            `goaldatapoints/recentRateChanges`,
            (data) => data.data
        );

    GetPlayerLifetimeKipsEarned = (): Promise<number> =>
        ClientDataProvider.GetValueAsync(
            `player/kips/lifetimeearned`,
            (data) => data.data
        );

    GetPlayerLevelCurrent = (): Promise<number> =>
        ClientDataProvider.GetValueAsync(
            `player/level/current`,
            (data) => data.data
        );

    GetDiversity = (date: string): Promise<IDiversityDateDTO> =>
        ClientDataProvider.GetValueAsync(
            `goaldatapoints/diversity/date?date=${date}`,
            (data: IDiversityDateDTO) => data
        );

    CacheClearAsync = (): Promise<string> =>
        ClientDataProvider.PostValueAsync("cache/clear", {});

    PostYoutubeDownloadRequest = (
        url: string
    ): Promise<IYoutubeDownloadResult> =>
        ClientDataProvider.PostValueAsync("youtube/download", { url });

    PostYoutubeSearchRequest = (
        searchText: string
    ): Promise<IYoutubeSearchResult[]> =>
        ClientDataProvider.PostValueAsync("youtube/search", { searchText });

    CreateNewCombo = (name: string): Promise<{ result: string }> =>
        ClientDataProvider.PostValueAsync("player/identities/createCombo", {
            name,
        });

    ClaimComboBonus = (identityName: string): Promise<{ result: string }> =>
        ClientDataProvider.PostValueAsync("player/identities/claimComboBonus", {
            identityName,
        });

    RecentEmotions = (): Promise<IRecentEmotionsDTO> =>
        ClientDataProvider.GetValueAsync("player/emotions/recent");

    GoalStreakInfo = (): Promise<IGoalStreakInfoDTO[]> =>
        ClientDataProvider.GetValueAsync<IGoalStreakInfoDTO[]>(
            "goals/streakInfo"
        );

    DailyEmotions = (): Promise<IEmotionRecordDTO[]> => {
        return ClientDataProvider.GetValueAsync(`player/emotions/daily`);
    };

    WeeklyEmotions = (): Promise<IEmotionRecordDTO[]> => {
        return ClientDataProvider.GetValueAsync(`player/emotions/weekly`);
    };

    MonthlyEmotions = (): Promise<IEmotionRecordDTO[]> => {
        return ClientDataProvider.GetValueAsync(`player/emotions/monthly`);
    };

    PreviousKeyValueEntries = (
        sharedFunction: ESharedFunction
    ): Promise<IKeyValueLog[]> => {
        return ClientDataProvider.GetRecordsAsync(
            `keyvaluelog?key=eq.${ESharedFunction[sharedFunction]}&limit=5&order=id.desc`
        );
    };

    CICODailyCalories = (): Promise<ICICODailyCaloriesRecordDTO[]> => {
        return ClientDataProvider.GetRecordsAsync(`rpc/cico_daily_calories`);
    };

    KeyValueAverages = (keyname: string): Promise<IKeyValueAverageDTO[]> => {
        return ClientDataProvider.GetRecordsAsync(
            `rpc/keyvalue_daily_averages?keyname=${keyname}`
        );
    };

    PostUnsungHeroPointAsync = (task: string): Promise<void> => {
        return ClientDataProvider.PostValueAsync(`log/unsungHeroPoint`, {
            task,
        });
    };

    GetVisibleNodesAsync = (id: number): Promise<IWorkflowNode[]> => {
        return ClientDataProvider.GetValueAsync<IWorkflowNode[]>(
            `workflownodes/visible?id=${id}`
        );
    };

    GetDailySpendRecordsAsync = (): Promise<IDailySpend[]> =>
        ClientDataProvider.GetRecordsAsync<IDailySpend>(resourceNameDailySpend);

    GetIsQuotable = (id: number): Promise<{ data: boolean }> => {
        return ClientDataProvider.GetValueAsync<{ data: boolean }>(
            `workflownodes/isquotable?id=${id}`
        );
    };

    GetPathToHomeNodeAsync = (id: number): Promise<IWorkflowNode[]> => {
        return ClientDataProvider.GetValueAsync<IWorkflowNode[]>(
            `workflownodes/pathtohomenode?id=${id}`
        );
    };

    GetBookOfMindProgressData = (): Promise<IRunningTotal[]> => {
        return ClientDataProvider.GetValueAsync<IRunningTotal[]>(
            `workflownodes/bookofmind/progress`
        );
    };

    GetBookOfMindSpeechCacheStatsAsync = (): Promise<IBookOfMindSpeechCacheStats> =>
        ClientDataProvider.GetValueAsync<IBookOfMindSpeechCacheStats>(
            "workflownodes/bookofmind/cacheStats"
        );

    SynthesizeBookOfMindSpeechAsync = (
        text: string,
        voiceName?: string,
        speechSettings?: {
            speakingRate: number;
            pitch: number;
        }
    ): Promise<Blob> =>
        ClientDataProvider.PostAudioAsync("workflownodes/bookofmind/speech", {
            text,
            voiceName,
            ...speechSettings,
        });

    SynthesizeBookOfMindPageSpeechAsync = (
        title: string,
        quotes: string[],
        voiceName?: string,
        speechSettings?: {
            speakingRate: number;
            pitch: number;
        },
        quoteSpeechSettings?: Array<{
            voiceName: string;
            speakingRate: number;
            pitch: number;
        }>,
        includeTitle = true
    ): Promise<Blob> =>
        ClientDataProvider.PostAudioAsync(
            "workflownodes/bookofmind/pageSpeech",
            {
                title,
                quotes,
                voiceName,
                ...speechSettings,
                quoteSpeechSettings,
                includeTitle,
            }
        );

    StartBookOfMindPageSpeechAsync = (
        title: string,
        quotes: string[],
        voiceName?: string,
        speechSettings?: {
            speakingRate: number;
            pitch: number;
        },
        quoteSpeechSettings?: Array<{
            voiceName: string;
            speakingRate: number;
            pitch: number;
        }>,
        includeTitle = true,
        clientRequestId?: string,
        pageAudioDiagnostics?: IBookOfMindPageAudioDiagnostics
    ): Promise<IBookOfMindPageSpeechJob> =>
        ClientDataProvider.PostBookOfMindPageSpeechJobAsync<IBookOfMindPageSpeechJob>(
            "workflownodes/bookofmind/pageSpeech",
            {
                title,
                quotes,
                voiceName,
                ...speechSettings,
                quoteSpeechSettings,
                includeTitle,
                clientRequestId,
                pageAudioDiagnostics,
            },
            {
                clientRequestId,
                titleLength: title.length,
                quoteCount: quotes.length,
                textLength: title.length + quotes.join("").length,
                voiceName,
                includeTitle,
                ...pageAudioDiagnostics,
            }
        );

    GetBookOfMindPageSpeechJobAsync = (
        jobId: string,
        clientRequestId?: string,
        shouldLogResponse = true
    ): Promise<IBookOfMindPageSpeechJob> =>
        ClientDataProvider.GetBookOfMindPageSpeechJobAsync<IBookOfMindPageSpeechJob>(
            `workflownodes/bookofmind/pageSpeech/jobs/${encodeURIComponent(
                jobId
            )}`,
            { clientRequestId, jobId },
            shouldLogResponse
        );

    GetBookOfMindPageSpeechAudioAsync = (
        jobId: string,
        clientRequestId?: string
    ): Promise<Blob> =>
        ClientDataProvider.GetAudioAsync(
            `workflownodes/bookofmind/pageSpeech/jobs/${encodeURIComponent(
                jobId
            )}/audio`,
            { clientRequestId, jobId }
        );

    GetRelatedBookOfMindQuotes = (
        id: number,
        limit?: number
    ): Promise<IRelatedBookOfMindQuotesResponse> => {
        const limitQuery = limit ? `&limit=${limit}` : "";
        return ClientDataProvider.GetValueAsync<IRelatedBookOfMindQuotesResponse>(
            `workflownodes/bookofmind/related?id=${id}${limitQuery}`
        );
    };

    WarmUpBookOfMindRelatedQuotes = (): Promise<{ warmingUp: boolean }> => {
        return ClientDataProvider.GetValueAsync<{ warmingUp: boolean }>(
            `workflownodes/bookofmind/warmup`
        );
    };

    RefreshBookOfMindRelatedQuotes = (): Promise<{ refreshing: boolean }> => {
        return ClientDataProvider.GetValueAsync<{ refreshing: boolean }>(
            `workflownodes/bookofmind/refresh`
        );
    };

    GetUserDetails = (
        CustomErrorHandler: (error: any) => any
    ): Promise<IUserDTO> => {
        return ClientDataProvider.GetValueAsync(
            `player/check`,
            (data) => data,
            CustomErrorHandler
        );
    };

    GetPirateWeatherData = (): Promise<IWeatherDataEntity[]> => {
        return ClientDataProvider.GetValueAsync<IWeatherDataEntity[]>(
            `weather/pirateweather`,
            (data) => data.data
        );
    };
}

export default new MotwLogicServer();
